import { useCallback, useEffect, useRef } from 'react';

/**
 * Defers a callback until the caller stops firing it.
 *
 * Used for the personalisation preflight: validating on every keystroke would
 * issue one request per character, and would also show an error for a field
 * the customer is still halfway through typing.
 *
 * The pending timer is cleared on unmount, so navigating away mid-edit does
 * not fire a request against a component that no longer exists.
 */
export function useDebouncedCallback<Args extends unknown[]>(
  callback: (...args: Args) => void | Promise<void>,
  delay = 400,
) {
  const timer = useRef<number | undefined>(undefined);

  // Held in a ref so the returned function is stable across renders while
  // still calling the latest closure — otherwise every render would reset
  // the debounce and it would never fire.
  const latest = useRef(callback);
  latest.current = callback;

  useEffect(
    () => () => {
      if (timer.current !== undefined) {
        window.clearTimeout(timer.current);
      }
    },
    [],
  );

  return useCallback(
    (...args: Args) => {
      if (timer.current !== undefined) {
        window.clearTimeout(timer.current);
      }

      timer.current = window.setTimeout(() => {
        void latest.current(...args);
      }, delay);
    },
    [delay],
  );
}
